import type { QueryClient } from '@tanstack/react-query';

import { meAccessQueryOptions, sidebarQueryOptions } from '../../../access';
import i18n from '../../../i18n';
import { languageChoiceOf, languageInUse } from '../../../i18n/languages';
import { DETAILS_UNREAD, SIGNED_OUT, currentUserIdOf, forgetCachedData, openSessionHere, replaceSession } from '../../../session';
import type { SignedInSession } from '../../../session';
import { resetDashboardSession } from '../../../store/dashboardSession';
// eslint-disable-next-line no-restricted-imports -- legacy: the sign-in landing page stays on the device until the session read serves it (backend ask)
import localStorageStore from '../../../store/localStorageStore';
import { analytics } from '../../analytics';
import { LOGIN_SESSION_START } from '../../analytics/events';
import { trackEvent } from '../../analytics/trackingMiddleware';
import type { OpenedSession, SessionLayout } from '../types/domain.types';

export type LoginMethod = 'passcode' | 'password';

const LANDING_PAGE_KEY = 'landingPage';

/**
 * The sidebar sign-in sent, in this tab's memory only, filed under the language the
 * person is about to see. Sign-in does not say which language its labels are in, so
 * it is shown at once and read again in that language straight away.
 */
const seedSidebar = (queryClient: QueryClient, session: OpenedSession, layout: SessionLayout): void => {
  const language = session.language === null ? languageInUse(i18n) : languageChoiceOf(session.language);
  queryClient.setQueryData(sidebarQueryOptions(language).queryKey, layout.menu ?? layout.sideBar, { updatedAt: 0 });
};

// The landing page sign-in names stays on the device until the server serves it on its own: a reload of / needs it.
const keepLandingPage = (previousUserId: string | null, session: OpenedSession): void => {
  // eslint-disable-next-line no-restricted-properties -- legacy: the sign-in landing page stays on the device until the session read serves it (backend ask)
  if (session.layout !== null) localStorageStore.setItem(LANDING_PAGE_KEY, session.layout.landingPage);
  // eslint-disable-next-line no-restricted-properties -- legacy: the sign-in landing page stays on the device until the session read serves it (backend ask)
  else if (previousUserId !== session.userId) localStorageStore.removeItem(LANDING_PAGE_KEY);
};

const signedInSessionOf = (session: OpenedSession): SignedInSession => ({
  status: 'signedIn',
  user: {
    id: session.userId,
    name: session.userName,
    email: session.email,
    isStaff: session.isStaff,
  },
  details: DETAILS_UNREAD,
});

/** Every way in — code, password, an issue link — opens the same session. */
export const startSession = (queryClient: QueryClient, session: OpenedSession, method: LoginMethod = 'passcode'): void => {
  const previousUserId = currentUserIdOf(queryClient);
  // Someone else signing in over another person's session ends that one here first, so nothing held for them carries over.
  if (previousUserId !== null && previousUserId !== session.userId) replaceSession(queryClient, SIGNED_OUT);
  forgetCachedData(queryClient);
  resetDashboardSession();
  keepLandingPage(previousUserId, session);
  if (session.access !== null) queryClient.setQueryData(meAccessQueryOptions().queryKey, session.access);
  if (session.layout !== null) seedSidebar(queryClient, session, session.layout);
  void analytics.identifyUser({ distinctId: session.userId, userProperties: { name: session.userName } });
  trackEvent(LOGIN_SESSION_START, { method });
  openSessionHere(queryClient, signedInSessionOf(session));
};
